import React from "react";
import cn from "classnames"
import {fetchAPI} from "../../lib/api";
import {motion} from "framer-motion"
import Layout from "../../components/Layout"
import EmblaCarousel from "../../components/EmblaCarousel";
import {svgPlaceholder, getCloudinaryUrl} from "../../lib/utils"
import Link from "next/link"
import {
  getNavbarItems,
  getFooterItems,
} from "../../lib/utils"

const getEmblaSlides = (gallery) =>
  gallery.length > 0 ? gallery.map(
    (item) => ({
      url: getCloudinaryUrl(item) || svgPlaceholder,
      alternativeText: item.attributes.alternativeText || "Projektbeispiel",
    })) : [
    {
      url: svgPlaceholder,
      alternativeText: "Projektbeispiel",
    },
  ]

function Work({navbarItems, work, footerItems}) {
  const [showCarouselInColor, setShowCarouselInColor] = React.useState(false)
  const [autoGrayscaleTimeoutId, setAutoGrayscaleTimeoutId] = React.useState(null)

  const handleCarouselClick = (_e) => {
    setShowCarouselInColor(true)
    if (autoGrayscaleTimeoutId) window.clearTimeout(autoGrayscaleTimeoutId)
    setAutoGrayscaleTimeoutId(
      window.setTimeout(() => setShowCarouselInColor(false), 3000)
    )
  }
  const wrapperRef = React.useRef(null)

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowCarouselInColor(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("scroll", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("scroll", handleClickOutside)
    }
  }, [wrapperRef])

  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1, transition: {duration: 1}}}
      exit={{opacity: 0}}
      className="container mx-auto my-header-margin"
    >
      <Layout navbarItems={navbarItems} footerItems={footerItems}>
        <div className="flex flex-col pb-20 lg:mt-20 lg:flex-row sm:mx-5">
          <div
            ref={wrapperRef}
            className={cn(
              {
                "shadow-my-1 filter-none": showCarouselInColor,
                "shadow-none grayscale": !showCarouselInColor,
              },
              "w-full transition-all duration-500 ease-in-out lg:w-1/3 xl:w-1/2 firefox-no-filter h-my-50vh lg:h-auto"
            )}
            onClick={handleCarouselClick}
            onTouchStart={handleCarouselClick}
          >
            <EmblaCarousel slides={getEmblaSlides(work.gallery.data)} />
          </div>
          <div className="flex flex-col lg:w-2/3 xl:w-1/2">
            <main className="w-full mt-12 text-right sm:pl-20 lg:mt-0">
              <h1 className="px-10 py-2 ml-auto text-2xl leading-tight tracking-wider text-white bg-teal-800 sm:px-4 lg:text-3xl font-title my-w-80">
                {work.title}
              </h1>
              <div
                className="mx-5 my-10 leading-normal text-gray-800 sm:mx-0"
                dangerouslySetInnerHTML={{__html: work.description}}
              />
            </main>
            <Link href="/" passHref scroll={false}>
              <button
                className="px-4 py-2 bg-white border border-l-0 border-gray-300 border-solid sm:border-l my-w-80 sm:w-auto lg:mt-auto lg:ml-20 lg:mb-0 focus:outline-none hover:bg-gray-100"
                aria-label="Zurück zur Startseite"
              >
                Home
              </button>
            </Link>
          </div>
        </div>
      </Layout>
    </motion.div>
  )
}

export async function getStaticPaths() {
  const works = await fetchAPI(`/works`, {populate: "*"})
  const paths = works.data.map((work) => `/leistungen/${work.attributes.slug}`)

  return {
    paths: paths || [],
    fallback: false,
  }
}

export async function getStaticProps({params}) {
  const [aboutRes, heroRes, privacyRes, impressumRes, servicesRes, contactRes] = await Promise.all([
    fetchAPI("/section-about", {populate: "*"}),
    fetchAPI("/section-hero", {populate: "*"}),
    fetchAPI("/section-privacy", {populate: "*"}),
    fetchAPI("/section-impressum", {populate: "*"}),
    fetchAPI("/section-services", {populate: "*"}),
    fetchAPI("/section-contact", {populate: "*"}),
  ]);

  // this is done with help of slugify extra package
  const workData = await fetchAPI(`/slugify/slugs/work/${params.slug}`, {populate: "*"})

  const navbarItems = getNavbarItems({
    sectionHero: heroRes.data.attributes,
    sectionAbout: aboutRes.data.attributes,
    sectionServices: servicesRes.data.attributes,
    sectionContact: contactRes.data.attributes,
  });

  const footerItems = getFooterItems({
    sectionPrivacy: privacyRes.data.attributes,
    sectionImpressum: impressumRes.data.attributes,
  });

  return {
    props: {
      navbarItems: navbarItems,
      work: workData.data.attributes,
      footerItems: footerItems,
    },
  }
}

export default Work
